import { useState } from "react";
import { ClipboardEdit, Loader2 } from "lucide-react";
import { getApiErrorMessage } from "@project/lib/api-error";
import { projectToast } from "@project/lib/toast";
import { useI18n } from "@project/lib/use-i18n";
import { HERO_IMAGES } from "@project/lib/stock-images";
import { VisitorFormFields } from "@project/admin/components/visitor-form-fields";
import { Button } from "@project/components/ui/button";
import { visitorInquiryService } from "@project/services/user/visitor-inquiry.service";
import {
  buildVisitorInquiryPayload,
  validateVisitorInquiryForm,
} from "@project/types/visitor-inquiry";
import { visitorDefaultValues } from "@project/types/visitor";
import { PageShell, PageHero } from "@project/user/components/page-shell";
import { Reveal } from "@project/user/components/motion";
import { VisitorSuccessDialog } from "@project/user/components/visitor-success-dialog";

function initialForm(): Record<string, unknown> {
  return { ...visitorDefaultValues() };
}

export default function VisitorInquiryPage() {
  const { t } = useI18n();
  const [form, setForm] = useState<Record<string, unknown> | null>(initialForm);
  const [busy, setBusy] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [visitorName, setVisitorName] = useState<string | undefined>();

  const handleSuccessOpenChange = (open: boolean) => {
    setSuccessOpen(open);
    if (!open) {
      setVisitorName(undefined);
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form) {
      return;
    }

    const validationError = validateVisitorInquiryForm(form);
    if (validationError) {
      projectToast.error(
        t(`account.visitorInquiryForm.validation.${validationError}`)
      );
      return;
    }

    setBusy(true);
    try {
      const payload = buildVisitorInquiryPayload(form);
      await visitorInquiryService.createInquiry(payload);
      setVisitorName(payload.visitor_name);
      setForm(initialForm());
      setSuccessOpen(true);
    } catch (error) {
      projectToast.error(
        getApiErrorMessage(error, t("account.visitorInquiryForm.toast.error"))
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageShell>
      <VisitorSuccessDialog
        open={successOpen}
        onOpenChange={handleSuccessOpenChange}
        visitorName={visitorName}
      />
      <PageHero
        eyebrow={t("account.visitorInquiryForm.eyebrow")}
        title={t("account.visitorInquiryForm.title")}
        subtitle={t("account.visitorInquiryForm.subtitle")}
        image={HERO_IMAGES.contact}
      />
      <section className="mx-auto max-w-4xl px-4 py-8 sm:py-12">
        <Reveal variant="morph">
          <form
            noValidate
            onSubmit={submit}
            className="anim-fade-up space-y-6 rounded-2xl border border-border bg-card p-5 shadow-sm sm:p-8"
            style={{ animationDelay: "200ms" }}
          >
            <VisitorFormFields
              editing={form}
              setEditing={setForm}
              variant="inquiry"
            />
            <div className="flex flex-col-reverse gap-3 border-t border-border pt-5 sm:flex-row sm:justify-end">
              <Button
                type="button"
                variant="outline"
                className="rounded-xl"
                disabled={busy}
                onClick={() => setForm(initialForm())}
              >
                {t("account.visitorInquiryForm.reset")}
              </Button>
              <Button
                type="submit"
                disabled={busy}
                className="hover:bg-primary/90 rounded-xl bg-primary text-primary-foreground"
              >
                {busy ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <ClipboardEdit className="mr-2 h-4 w-4" />
                )}
                {busy
                  ? t("account.visitorInquiryForm.submitting")
                  : t("account.visitorInquiryForm.submit")}
              </Button>
            </div>
          </form>
        </Reveal>
      </section>
    </PageShell>
  );
}
